import axios from "axios";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { FaChevronCircleLeft, FaChevronCircleRight, FaChevronDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { useAuth } from "../context/AuthContext";

const FIELDS_AR = {
  1: "الزراعة",
  2: "الآداب",
  3: "الأعمال",
  4: "علوم الحاسوب وتكنولوجيا المعلومات",
  5: "التربية",
  6: "الهندسة",
  7: "العلوم الإنسانية",
  8: "القانون",
  9: "الطب",
  10: "العلوم الطبيعية",
  11: "العلوم الاجتماعية",
  12: "أخرى",
};

const DECLARATION_TEXT = (
  <>
    أقر بأن جميع المعلومات المقدمة في هذا الطلب صحيحة ودقيقة، وأنني على استعداد للالتزام بمتطلبات برنامج
    المدربين وشروطه وأحكامه.
    <br />
    أفهم أن تقديم معلومات غير صحيحة قد يؤدي إلى رفض طلبي أو إلغاء مشاركتي.
  </>
);

const RadioGroup = ({ name, options, value, onChange, onBlur }) => (
  <div className="flex flex-wrap gap-5 pt-1">
    {options.map((opt) => (
      <label key={opt.value} className="flex cursor-pointer items-center gap-2">
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(name, opt.value)}
          onBlur={() => onBlur(name, true)}
          className="h-4 w-4 accent-purple-600"
        />
        <span className="text-sm text-[#616262]">{opt.label}</span>
      </label>
    ))}
  </div>
);

const TrainerRegister = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [governorates, setGovernorates] = useState([]);
  const [allCenters, setAllCenters] = useState({});
  const [futureStations, setFutureStations] = useState([]);
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formik = useFormik({
    initialValues: {
      governorate_id: "",
      center_id: "",
      current_role: "", // 1 | 2 | 3
      status_desc: "", // required when current_role === "3"
      station_id: "", // required when current_role === "2" (future stations trainer)
      academic_specialization: "",
      highest_qualification: "", // 1 | 2 | 3 | 4
      academic_qualification_desc: "", // required when highest_qualification === "4"
      has_it_background: "",
      completed_ai_course: "",
      commit_5_days_training: "",
      commit_10_hours_monthly: "",
      declaration: false,
    },
    validationSchema: Yup.object({
      governorate_id: Yup.string().required("مطلوب"),
      center_id: Yup.string().required("مطلوب"),
      current_role: Yup.string().required("مطلوب"),
      status_desc: Yup.string().when("current_role", {
        is: "3",
        then: (s) => s.required("مطلوب"),
        otherwise: (s) => s.optional(),
      }),
      station_id: Yup.string().when("current_role", {
        is: "2",
        then: (s) => s.required("مطلوب"),
        otherwise: (s) => s.optional(),
      }),
      academic_specialization: Yup.string().required("مطلوب"),
      highest_qualification: Yup.string().required("مطلوب"),
      academic_qualification_desc: Yup.string().when("highest_qualification", {
        is: "4",
        then: (s) => s.required("مطلوب"),
        otherwise: (s) => s.optional(),
      }),
      has_it_background: Yup.string().required("مطلوب"),
      completed_ai_course: Yup.string().required("مطلوب"),
      commit_5_days_training: Yup.string().required("مطلوب"),
      commit_10_hours_monthly: Yup.string().required("مطلوب"),
      declaration: Yup.boolean().oneOf([true], "يجب الموافقة على الإقرار للمتابعة"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setApiError("");
      try {
        const payload = {
          governorate_id: Number(values.governorate_id),
          center_id: Number(values.center_id),
          status: Number(values.current_role),
          ...(values.current_role === "3" && { status_desc: values.status_desc }),
          station_id: values.current_role === "2" ? Number(values.station_id) : null,
          major: values.academic_specialization,
          academic_qualification: Number(values.highest_qualification),
          ...(values.highest_qualification === "4" && {
            academic_qualification_desc: values.academic_qualification_desc,
          }),
          have_tech_info: values.has_it_background === "yes",
          is_ai_course_completed: values.completed_ai_course === "yes",
          is_5_days_training: values.commit_5_days_training === "yes",
        };
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/trainer`, payload, {
          headers: {
            lang: "ar",
            Authorization: `Bearer ${user?.token}`,
          },
        });
        const meRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/auth/me`, {
          headers: { lang: "ar", Authorization: `Bearer ${user?.token}` },
        });
        const freshUser = { ...user, ...meRes?.data?.data };
        localStorage.setItem("user", JSON.stringify(freshUser));
        setUser(freshUser);

        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          navigate("/");
        }, 3000);
      } catch (error) {
        setApiError(error.response?.data?.message || "حدث خطأ، يرجى المحاولة لاحقًا");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [govRes, centersRes, stationsRes, meRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/governorates`, {
            headers: { lang: "ar" },
          }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/centers`, {
            headers: { lang: "ar" },
          }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/future-station-promo-codes`, {
            headers: { lang: "ar" },
          }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/auth/me`, {
            headers: { lang: "ar", Authorization: `Bearer ${user?.token}` },
          }),
        ]);

        setGovernorates(govRes?.data?.data || []);
        setAllCenters(centersRes?.data?.data || {});
        setFutureStations(stationsRes?.data || []);

        const me = meRes?.data?.data;
        if (me?.governorate?.id) {
          formik.setFieldValue("governorate_id", String(me.governorate.id));
        }
        // Pre-fill academic specialization: use custom name if set, otherwise look up from field ID
        const majorId = me?.student_major_id || me?.other_major_id;
        const majorName = me?.student_major_name || me?.other_major_name || FIELDS_AR[majorId];
        if (majorName) {
          formik.setFieldValue("academic_specialization", majorName);
        }
        // Pre-fill qualification: map profile education_level_id → trainer form values
        // 2 = Bachelor's → "2", 3 = Master's → "3"
        const qualMap = { 2: "2", 3: "3" };
        if (me?.education_level_id && qualMap[me.education_level_id]) {
          formik.setFieldValue("highest_qualification", qualMap[me.education_level_id]);
        }
      } catch {
        setApiError("تعذّر تحميل البيانات. يرجى المحاولة لاحقًا.");
      }
    };
    fetchData();
  }, []);

  // Reset center selection when governorate changes
  useEffect(() => {
    formik.setFieldValue("center_id", "");
  }, [formik.values.governorate_id]);

  // Clear station selection when the role is not "future stations trainer"
  useEffect(() => {
    if (formik.values.current_role !== "2" && formik.values.station_id !== "") {
      formik.setFieldValue("station_id", "");
    }
  }, [formik.values.current_role]);

  const filteredCenters = formik.values.governorate_id ? allCenters[formik.values.governorate_id] || [] : [];

  const ErrorMsg = ({ name }) =>
    formik.touched[name] && formik.errors[name] ? (
      <span className="text-xs text-red-500">{formik.errors[name]}</span>
    ) : null;

  const fieldClass = (name) =>
    `w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 focus:outline-none ${
      formik.touched[name] && formik.errors[name]
        ? "border-red-500 focus:border-red-500"
        : "border-gray-200 focus:border-purple-500 focus:ring-1 focus:ring-purple-300"
    }`;

  const selectClass = (name) =>
    `w-full appearance-none rounded-xl border-2 bg-white px-3 py-2 text-sm text-[#616262] transition-all duration-300 focus:outline-none ${
      formik.touched[name] && formik.errors[name]
        ? "border-red-500 focus:border-red-500"
        : "border-gray-200 focus:border-purple-500 focus:ring-1 focus:ring-purple-300"
    }`;

  return (
    <>
      {/* Full-width header bar */}
      <div className="bg-white px-6" dir="rtl">
        <div className="flex items-center justify-between px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-purple-500 hover:text-purple-700"
          >
            <FaChevronCircleRight size={18} className="text-purple-500" />
            العودة
          </button>
          <img
            src="/logo-blue.svg"
            alt="Logo"
            onClick={() => navigate("/")}
            className="h-10 w-auto cursor-pointer"
          />
        </div>
      </div>

      <div className="mx-auto max-w-3xl space-y-6 p-4" dir="rtl">
        {/* Page header banner */}
        <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white shadow-md">
          <h1 className="text-2xl font-bold">التسجيل كمدرب</h1>
          <p className="mt-1 text-sm text-purple-100">
            يرجى تعبئة جميع الحقول المطلوبة أدناه للتقدم إلى برنامج المدربين.
          </p>
        </div>

        {/* Form card */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          {apiError && (
            <div className="mb-5 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
              {apiError}
            </div>
          )}

          <form onSubmit={formik.handleSubmit} className="space-y-6">
            {/* 1. Governorate */}
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <label className="text-sm font-bold text-gray-700"> المحافظة / اللواء</label>
                <ErrorMsg name="governorate_id" />
              </div>
              <div className="relative">
                <select
                  name="governorate_id"
                  value={formik.values.governorate_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={selectClass("governorate_id")}
                >
                  <option value="" disabled>
                    اختر المحافظة
                  </option>
                  {governorates.map((gov) => (
                    <option key={gov.id} value={gov.id}>
                      {gov.name}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            {/* 2. Youth Center (cascading) */}
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <label className="text-sm font-bold text-gray-700">أقرب مركز شباب إليك</label>
                <ErrorMsg name="center_id" />
              </div>
              <div className="relative">
                <select
                  name="center_id"
                  value={formik.values.center_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={!formik.values.governorate_id}
                  className={`${selectClass("center_id")} disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  <option value="" disabled>
                    {!formik.values.governorate_id
                      ? "اختر المحافظة أولاً"
                      : filteredCenters.length === 0
                        ? "لا توجد مراكز متاحة"
                        : "اختر مركز الشباب"}
                  </option>
                  {filteredCenters.map((center) => (
                    <option key={center.id} value={center.id}>
                      {center.name}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            {/* 3. Current Role */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <label className="text-sm font-bold text-gray-700"> الصفة الحالية</label>
                <ErrorMsg name="current_role" />
              </div>
              <RadioGroup
                name="current_role"
                value={formik.values.current_role}
                onChange={formik.setFieldValue}
                onBlur={formik.setFieldTouched}
                options={[
                  { value: "1", label: "موظف مركز شباب" },
                  { value: "2", label: "مدرب محطات المستقبل" },
                  { value: "3", label: "أخرى" },
                ]}
              />
              {formik.values.current_role === "2" && (
                <div className="mt-3">
                  <div className="mb-1.5 flex items-center gap-2">
                    <label className="text-sm font-bold text-gray-700">محطة المستقبل</label>
                    <ErrorMsg name="station_id" />
                  </div>
                  <div className="relative">
                    <select
                      name="station_id"
                      value={formik.values.station_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={selectClass("station_id")}
                    >
                      <option value="" disabled>
                        اختر المحطة
                      </option>
                      {futureStations.map((station) => (
                        <option key={station.id} value={station.id}>
                          {station.description}
                        </option>
                      ))}
                    </select>
                    <FaChevronDown className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              )}
              {formik.values.current_role === "3" && (
                <div className="mt-3">
                  <input
                    type="text"
                    name="status_desc"
                    placeholder="يرجى التحديد"
                    value={formik.values.status_desc}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={fieldClass("status_desc")}
                  />
                  <ErrorMsg name="status_desc" />
                </div>
              )}
            </div>

            {/* 4. Academic Specialization */}
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <label className="text-sm font-bold text-gray-700"> التخصص الأكاديمي</label>
                <ErrorMsg name="academic_specialization" />
              </div>
              <input
                type="text"
                name="academic_specialization"
                placeholder="أدخل تخصصك الأكاديمي"
                value={formik.values.academic_specialization}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={fieldClass("academic_specialization")}
              />
            </div>

            {/* 5. Highest Qualification */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <label className="text-sm font-bold text-gray-700"> أعلى مؤهل علمي</label>
                <ErrorMsg name="highest_qualification" />
              </div>
              <RadioGroup
                name="highest_qualification"
                value={formik.values.highest_qualification}
                onChange={formik.setFieldValue}
                onBlur={formik.setFieldTouched}
                options={[
                  { value: "1", label: "دبلوم" },
                  { value: "2", label: "بكالوريوس" },
                  { value: "3", label: "ماجستير" },
                  { value: "4", label: "أخرى" },
                ]}
              />
              {formik.values.highest_qualification === "4" && (
                <div className="mt-3">
                  <input
                    type="text"
                    name="academic_qualification_desc"
                    placeholder="يرجى التحديد"
                    value={formik.values.academic_qualification_desc}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={fieldClass("academic_qualification_desc")}
                  />
                  <ErrorMsg name="academic_qualification_desc" />
                </div>
              )}
            </div>

            <div className="border-t border-gray-100" />

            {/* 6–9: Yes/No questions */}
            {[
              {
                name: "has_it_background",
                label: "هل لديك خلفية في تكنولوجيا المعلومات؟",
              },
              {
                name: "completed_ai_course",
                label: "هل أتممت مسار أساسيات الذكاء الاصطناعي في JoCodes؟",
              },
              {
                name: "commit_5_days_training",
                label: "القدرة على الالتزام بحضور البرنامج التدريبي كاملا لمدة (5) أيام؟",
              },
              {
                name: "commit_10_hours_monthly",
                label: "القدرة على الالتزام بتنفيذ أنشطة البرنامج داخل المحافظة بمعدل 10 ساعات شهريًا؟",
              },
            ].map(({ num, name, label }) => (
              <div key={name}>
                <div className="mb-2 flex items-center gap-2">
                  <label className="text-sm font-bold text-gray-700">{label}</label>
                  <ErrorMsg name={name} />
                </div>
                <RadioGroup
                  name={name}
                  value={formik.values[name]}
                  onChange={formik.setFieldValue}
                  onBlur={formik.setFieldTouched}
                  options={[
                    { value: "yes", label: "نعم" },
                    { value: "no", label: "لا" },
                  ]}
                />
              </div>
            ))}

            <div className="border-t border-gray-100" />

            {/* 10. Declaration */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="mb-3 text-sm font-bold text-gray-700"> إقرار المتقدم</p>
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="declaration"
                  name="declaration"
                  checked={formik.values.declaration}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-purple-600"
                />
                <label htmlFor="declaration" className="cursor-pointer text-sm leading-relaxed text-gray-600">
                  {DECLARATION_TEXT}
                </label>
              </div>
              {formik.touched.declaration && formik.errors.declaration && (
                <p className="mt-2 text-xs text-red-500">{formik.errors.declaration}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !formik.values.declaration}
              className={`w-full rounded-full py-3 text-sm font-semibold text-white transition ${
                loading || !formik.values.declaration
                  ? "cursor-not-allowed bg-purple-300"
                  : "bg-purple-600 hover:bg-purple-700 active:scale-[0.98]"
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  جار الإرسال...
                </span>
              ) : (
                "تقدّم للبرنامج"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Success modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="flex flex-col items-center rounded-2xl bg-white p-8 shadow-2xl" dir="rtl">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-7 w-7 text-green-600"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-bold text-green-600">تم تقديم طلبك بنجاح!</p>
            <p className="mt-1 text-sm text-gray-500">سيتم التواصل معك قريباً.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default TrainerRegister;
